/**
 * PlanchadoDialog — muestra el comando `uci` YA ARMADO para dejar un RUT en
 * golden. SOLO muestra: no ejecuta nada (respeta AROC_MODO / modo observacion).
 * El administrador copia el comando y lo corre por SSH (portproxy 22NNN).
 *
 * No lleva credenciales: el comando es config uci (zona/NTP), el login SSH va
 * aparte y nunca se renderiza aqui.
 */
import { useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  Loader2,
  Wrench,
  X,
  Zap,
} from "lucide-react";

import { ApiError } from "@/api/client";
import { usePlancharAplicar } from "@/hooks/useConfig";

interface PlanchadoDialogProps {
  open: boolean;
  onClose: () => void;
  /** Id del equipo, para el botón "Planchar y aplicar" (null = deshabilitado). */
  deviceId: string | null;
  /** Ej: "Toluca · 10.0.0.1:24002". */
  targetLabel: string;
  loading: boolean;
  errorMessage: string | null;
  enGolden: boolean;
  comandos: string;
  /**
   * "Planchar y aplicar" ESCRIBE al equipo. RUT y biométricos lo soportan,
   * cada uno detrás de su propio candado en el backend; si está apagado,
   * responde 409 con el nombre de la variable. No se ofrece donde el backend
   * no pueda ejecutarlo: un botón que siempre falla es peor que no tenerlo.
   */
  permiteAplicar?: boolean;
  /** Dónde se corren los comandos ("la shell del RUT (SSH)"). */
  dondeCorrerlos?: string;
  /** Cómo se llama el equipo en los avisos: "router", "biométrico"... */
  nombreEquipo?: string;
}

export function PlanchadoDialog({
  open,
  onClose,
  deviceId,
  targetLabel,
  loading,
  errorMessage,
  enGolden,
  comandos,
  permiteAplicar = true,
  dondeCorrerlos = "la shell del RUT (SSH)",
  nombreEquipo = "router",
}: PlanchadoDialogProps) {
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const aplicar = usePlancharAplicar();

  if (!open) return null;

  const aplicarError =
    aplicar.error instanceof ApiError
      ? aplicar.error.message
      : aplicar.error
        ? "No se pudo aplicar."
        : null;
  const resultado = aplicar.data;

  const handleClose = () => {
    aplicar.reset();
    setConfirming(false);
    setCopied(false);
    onClose();
  };

  const handleCopy = () => {
    void navigator.clipboard.writeText(comandos).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="planchado-title"
    >
      <div
        className="w-full max-w-2xl rounded-lg border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="size-5 text-emerald-600" />
            <div>
              <h2 id="planchado-title" className="text-lg font-semibold">
                Planchar a configuración golden
              </h2>
              <p className="text-sm text-muted-foreground">{targetLabel}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </button>
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Generando comandos…
          </p>
        ) : errorMessage ? (
          <p className="rounded-md bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-400">
            {errorMessage}
          </p>
        ) : enGolden ? (
          <p className="rounded-md bg-emerald-500/10 px-3 py-6 text-center text-sm text-emerald-700 dark:text-emerald-400">
            Este equipo ya está en golden. No hay nada que planchar.
          </p>
        ) : (
          <>
            <p className="mb-2 text-sm text-muted-foreground">
              Copia y corre estos comandos en {dondeCorrerlos}. Sólo aplican
              configuración; no incluyen credenciales.
            </p>
            <div className="relative">
              <pre className="max-h-80 overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
                {comandos}
              </pre>
              <button
                type="button"
                onClick={handleCopy}
                className="absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1 text-xs font-medium shadow-sm transition-colors hover:bg-muted"
              >
                {copied ? (
                  <>
                    <Check className="size-3.5 text-emerald-600" /> Copiado
                  </>
                ) : (
                  <>
                    <Copy className="size-3.5" /> Copiar
                  </>
                )}
              </button>
            </div>

            {/* Aplicar directo: ESCRIBE al equipo del cliente. El backend lo gatea
                con RUT_PLANCHADO_APPLY o BIO_PLANCHADO_APPLY segun la familia
                (+ admin); si esta apagado, responde 409 diciendo cual. */}
            {permiteAplicar ? (
            <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              {resultado ? (
                <div className="text-sm">
                  <p
                    className={
                      resultado.aplicado
                        ? "font-medium text-emerald-700 dark:text-emerald-400"
                        : "font-medium text-rose-700 dark:text-rose-400"
                    }
                  >
                    {resultado.aplicado
                      ? `✓ Aplicado en el ${nombreEquipo}`
                      : `No aplicado${resultado.motivo ? ` (${resultado.motivo})` : ""}`}
                  </p>
                  {resultado.salida ? (
                    <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted/40 p-2 font-mono text-[11px]">
                      {resultado.salida}
                    </pre>
                  ) : null}
                </div>
              ) : aplicarError ? (
                <p className="text-sm text-rose-700 dark:text-rose-400">
                  {aplicarError}
                </p>
              ) : confirming ? (
                <div className="flex flex-col gap-2 text-sm">
                  <span className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="size-4" /> Esto ESCRIBE la
                    configuración en el {nombreEquipo} del cliente. ¿Continuar?
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={aplicar.isPending || !deviceId}
                      onClick={() => deviceId && aplicar.mutate(deviceId)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
                    >
                      {aplicar.isPending ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Zap className="size-3.5" />
                      )}
                      Sí, aplicar
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-muted-foreground">
                    ¿No quieres pegarlo a mano? Aplícalo directo (escribe al
                    router).
                  </span>
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-amber-500/40 px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-500/10 dark:text-amber-400"
                  >
                    <Zap className="size-3.5" /> Planchar y aplicar
                  </button>
                </div>
              )}
            </div>
            ) : null}
          </>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
